import React, { useEffect, useMemo, useReducer } from 'react';

import { unstable, contexts, caches } from '@broadleaf/admin-components';

import qs from 'query-string';
import { useHistory, useLocation } from 'react-router-dom';
import { isEmpty, omit } from 'lodash';

const {
  useCurrentTenant,
  AuthService,
  TenantService,
  tenantReducer,
  useGatewayState,
  useLocationCache,
  tenantActions
} = unstable;

const { TenantCache } = caches;
const { TenantContext } = contexts;
const { resolveAdminTenant } = TenantService;
const { setTenant, setApplication, setApplicationId } = tenantActions;

const cacheKey = `tenant_resolver_${window.location.hostname}`;

/**
 * This is a customization of AdminWeb's TenantGateway to fix an issue with
 * the application ID not being set when the user logs in via the admin token endpoint.
 */
const AuroraTenantGateway = props => {
  const gateway = useGatewayState(props.ready);
  const context = useTenantState(gateway);
  return (
    <TenantContext.Provider value={context}>
      {props.children(gateway)}
    </TenantContext.Provider>
  );
};

AuroraTenantGateway.defaultProps = {
  ready: true
};

function useTenantState(gateway) {
  const isReady = gateway.isReady();
  const isFinished = gateway.isFinished();
  const { setError, setStatus } = gateway;
  const context = useReducer(tenantReducer, useInitialState());
  const [state, dispatch] = context;
  const [appParam, setAppParam] = React.useState(null);

  const currentApplication = useMemo(() => {
    return state.applicationId
      ? state.applicationById[state.applicationId]
      : null;
  }, [state.applicationById, state.applicationId]);

  // synchronize the application choice with the browser location, e.g. back/forward will swap applications
  const locationCachedApplication = useLocationCache(
    'currentApplication',
    currentApplication
  );

  useSetApplication({
    isFinished,
    state,
    dispatch,
    setAppParam,
    locationCachedApplication,
    appParam
  });

  useSetApplicationByQueryParameter(isFinished, dispatch, setAppParam);

  // cache the current application ID in storage
  useEffect(() => {
    // only cache when tenant has been resolved
    if (isFinished) {
      TenantCache.put(cacheKey, {
        ...state,
        applicationById: trimApplicationById(state.applicationById)
      });
    }
  }, [currentApplication, isFinished, state]);

  useEffect(
    () => {
      if (isReady) {
        (async () => {
          if (state.tenantId) {
            // if the tenant is already resolved, i.e. from cache, we are finished
            setStatus(GatewayStatus.FINISHED);
            return;
          }

          setStatus(GatewayStatus.LOADING);

          try {
            const { tenant } = await resolveAdminTenant();

            dispatch(setTenant(tenant));
            setStatus(GatewayStatus.FINISHED);
          } catch (error) {
            setError(error);
            setStatus(GatewayStatus.ERROR);
          }
        })();
      }
    },
    // eslint-disable-next-line
    [isReady]
  );

  return context;
}

function useSetApplication({ dispatch, setAppParam, appParam, state }) {
  const user = AuthService.getState().user;
  const tenant = useCurrentTenant();

  // Fix to set the application when the application ID is provided in the URL
  // This keeps up with the setApplicationId URL parameter. In OOB flow, this never gets used unless the user
  // is already logged in. The app permission check is done before all required data is loaded which causes
  // non-logged in users to always fail the application visibility check.
  useEffect(() => {
    if (!appParam || !user) {
      return;
    }
    if (state.applicationId === appParam) {
      // already set
      return;
    }
    const apps = isEmpty(state.applicationById) ? {} : state.applicationById;
    const requestedApp = apps[appParam] || {};
    const isAuthorizedForApp =
      user.applicationIds.includes(requestedApp.id) ||
      (user.tenantAccess && user.applicationAccess);

    if (!isEmpty(requestedApp) && user && isAuthorizedForApp) {
      dispatch(setApplication(requestedApp));
      setAppParam(undefined);
    }
  }, [
    appParam,
    setAppParam,
    dispatch,
    user,
    tenant,
    state.applicationId,
    state.applicationIds,
    state.applicationById
  ]);
}

function useInitialState() {
  return useMemo(() => {
    const cachedState = TenantCache.get(cacheKey);
    if (!cachedState) {
      return initialTenantState;
    }

    const params = window.location.search && qs.parse(window.location.search);
    if (!!params && !!params['tenant']) {
      // if we detect a tenant parameter, then we will ignore the cache, as we are trying to resolve a new tenant
      return initialTenantState;
    }

    return cachedState;
  }, []);
}

function trimApplicationById(applicationById) {
  const trimmedApplicationById = {};
  for (const appId in applicationById) {
    const app = applicationById[appId];
    trimmedApplicationById[appId] = {
      id: app.id,
      name: app.name,
      identifierType: app.identifierType,
      identifierValue: app.identifierValue,
      domainType: app.domainType,
      domainPrefix: app.domainPrefix,
      fullDomain: app.fullDomain,
      dealerNetworkId: app.dealerNetworkId,
      nationalSite: app.nationalSite,
      natlSiteMembers: app.natlSiteMembers,
      deactivated: app.deactivated,
      dealerNetwork: app.dealerNetwork,
      accountNumber: app.accountNumber,
      locationNumber: app.locationNumber,
      adminLogoAsset: app.adminLogoAsset
        ? {
            type: app.adminLogoAsset.type,
            embedCode: app.adminLogoAsset.embedCode,
            contentUrl: app.adminLogoAsset.contentUrl,
            altText: app.adminLogoAsset.altText
          }
        : undefined,
      logoAsset: app.logoAsset
        ? {
            type: app.logoAsset.type,
            embedCode: app.logoAsset.embedCode,
            contentUrl: app.logoAsset.contentUrl,
            altText: app.logoAsset.altText
          }
        : undefined,
      attributes: app.attributes
        ? {
            shopAsDealerNetwork: app.attributes.shopAsDealerNetwork
          }
        : undefined
    };
  }
  return trimmedApplicationById;
}

/**
 * This hook ensures that the application ID is set if a query parameter with
 * the name `setApplicationId` is provided.
 *
 * @param {boolean} isFinished whether or not finished with initial tenancy data load
 * @param {Function} dispatch the dispatch function
 * @param setAppParam
 */
function useSetApplicationByQueryParameter(isFinished, dispatch, setAppParam) {
  const location = useLocation();
  const history = useHistory();
  const query = qs.parse(location.search);
  const { user } = AuthService.getState();
  useEffect(() => {
    if (user && isFinished && !!query.setApplicationId) {
      dispatch(setApplicationId(query.setApplicationId));
      setAppParam(query.setApplicationId);
      history.replace({
        ...location,
        search: qs.stringify(omit(query, ['setApplicationId']))
      });
    }
    // eslint-disable-next-line
  }, [dispatch, history, isFinished, location, query, user]);
}

export const GatewayStatus = Object.freeze({
  // used to indicate the gateway has an error and should block rendering
  ERROR: 'ERROR',
  // used to indicate the gateway has finished initializing and that it was successful in doing so
  FINISHED: 'FINISHED',
  // used to indicate the gateway is loading and in-progress
  LOADING: 'LOADING',
  // used to indicate the gateway is active and ready to initialize
  READY: 'READY',
  // used to indicate the gateway is inactive and waiting to initialize
  WAITING: 'WAITING'
});

export const initialTenantState = {
  applicationById: {},
  applicationId: null,
  applicationIds: [],
  isFetchApplicationsQueued: false,
  tenantById: {},
  tenantId: null
};

export default AuroraTenantGateway;
